import { create } from 'zustand';
import Conversation from '@/types/conversationType';
import Issue from '@/types/issueType';
import Repository from '@/types/repositoryType';
import emptyConversation from '@/types/emptyConversation';
import emptyIssue from '@/types/emptyIssue';
import emptyRepository from '@/types/emptyRepository';
import useConversationStore from './conversationState';
import useUserStore from './userState';
import changePrAndIssueForIndividual, {
  setPrAndIssueForLoggedOut,
} from '@/helpers/changeIssueAndPrForIndividual';
import changePrAndIssueForOrg from '@/helpers/changeIssueAndPrForOrganization';
import useRepositoryInfoStore from './repositoryInfoState';
import ConversationReqRepository from '@/types/conversationReqRepositoryType';
import posthog from '@/lib/posthog';

interface SelectedStore {
  selectedRepository: Repository;
  selectedIssue: Issue;
  selectedConversation: Conversation;
  isCodeFeedbackOpen: boolean;
  isIssueModalOpen: boolean;
  isChatDisabled: boolean;
  setSelectedRepository: (
    repository: Repository,
    isLoggedOut: boolean
  ) => Promise<boolean>;
  setSelectedIssue: (issue: Issue, isLoggedOut: boolean) => Promise<boolean>;
  openIssueModal: (open: boolean) => boolean;
  disableChat: (disable: boolean) => boolean;
  openCodeFeedback: () => Promise<boolean>;
  abortController: AbortController | null;
  setAbortController: (controller: AbortController | null) => void;
}

const useSelectedStore = create<SelectedStore>()((set, get) => ({
  selectedRepository: emptyRepository,
  selectedIssue: emptyIssue,
  selectedConversation: emptyConversation,
  isIssueModalOpen: false,
  isChatDisabled: false,
  isCodeFeedbackOpen: false,

  setSelectedRepository: async (
    repository: Repository,
    isLoggedOut: boolean
  ) => {
    if (isLoggedOut) {
      // Track repository selection for logged out user
      posthog.capture('repository_selected', {
        repository_name: repository.Name,
        is_logged_out: true,
      });

      const addConversationForLoggedOutUser =
        useConversationStore.getState().addConversationForLoggedOutUser;
      const convReq: ConversationReqRepository = {
        UserUUID: '',
        RepositoryUUID: '',
        Name: repository.Name,
        VectorDBUrl: repository.VectorDBURL,
      };
      setPrAndIssueForLoggedOut(repository.Name);
      const storageKey = localStorage.getItem(
        `chat-messages-${repository.Name.replace('/', '-')}`
      );
      const conversationUUID = storageKey
        ? JSON.parse(storageKey)?.state?.messageList?.[0]?.ConversationUUID
        : '';
      const conversation: Conversation = await addConversationForLoggedOutUser(
        convReq,
        conversationUUID
      );
      set({
        selectedRepository: repository,
        selectedIssue: emptyIssue,
        selectedConversation: conversation,
        isCodeFeedbackOpen: false,
      });

      return true;
    }

    if (!repository || repository === emptyRepository) {
      return false;
    } else if (repository === get().selectedRepository) {
      return true;
    }

    // Track repository selection for logged in user
    const user = useUserStore.getState().user;
    posthog.capture('repository_selected', {
      repository_name: repository.Name,
      is_logged_out: false,
      github_username: user?.GithubUsername,
    });

    const setRepositoryDetails =
      useRepositoryInfoStore.getState().setRepositoryInfo;
    setRepositoryDetails(repository);

    const currentSelectedRepository = get().selectedRepository;
    // if its an user's repository we always change unless its the same repo
    if (!repository.OrganizationID) {
      changePrAndIssueForIndividual(repository);
    }
    // if it's an org repo we only change if old repo wasn't an org repo
    // two possible cases there - old repo was an empty repo or it was an user repo
    else if (
      currentSelectedRepository === emptyRepository ||
      !currentSelectedRepository.OrganizationID
    ) {
      changePrAndIssueForOrg(repository);
    }

    set({
      selectedRepository: repository,
      selectedIssue: emptyIssue,
      selectedConversation: emptyConversation,
      isCodeFeedbackOpen: false,
    });

    return true;
  },

  setSelectedIssue: async (issue: Issue, isLoggedOut: boolean) => {
    if (!issue || issue === emptyIssue) {
      return false;
    } else if (issue === get().selectedIssue) {
      return true;
    }

    // Track issue selection
    const user = useUserStore.getState().user;
    posthog.capture('issue_selected', {
      issue_title: issue.Title,
      issue_number: issue.TicketNumber,
      is_logged_out: isLoggedOut,
      github_username: user?.GithubUsername,
    });

    if (isLoggedOut) {
      set({
        selectedRepository: emptyRepository,
        selectedIssue: issue,
        selectedConversation: emptyConversation,
        isCodeFeedbackOpen: false,
      });
      return true;
    }

    set({
      selectedRepository: emptyRepository,
      selectedIssue: issue,
      selectedConversation: emptyConversation,
      isCodeFeedbackOpen: false,
    });
    return true;
  },

  openIssueModal: (modalState: boolean) => {
    // Track issue modal state change
    const user = useUserStore.getState().user;
    posthog.capture('issue_modal_state_changed', {
      is_open: modalState,
      github_username: user?.GithubUsername,
    });

    set({ isIssueModalOpen: modalState });
    return true;
  },

  disableChat: (disable: boolean) => {
    // Track chat state change (throttled to avoid rate limiting)
    try {
      const user = useUserStore.getState().user;
      // Only capture if state actually changed to avoid excessive calls
      const currentState = get().isChatDisabled;
      if (currentState !== disable) {
        posthog.capture('chat_state_changed', {
          is_disabled: disable,
          github_username: user?.GithubUsername,
        });
      }
    } catch (error) {
      // Silently handle PostHog rate limiting errors
      // PostHog already handles rate limiting internally
    }

    set({ isChatDisabled: disable });
    return true;
  },

  openCodeFeedback: async () => {
    if (get().isCodeFeedbackOpen) return true;

    // Track code feedback opened
    const user = useUserStore.getState().user;
    posthog.capture('code_feedback_opened', {
      github_username: user?.GithubUsername,
    });

    set({
      isChatDisabled: false,
      selectedRepository: emptyRepository,
      selectedIssue: emptyIssue,
      selectedConversation: emptyConversation,
      isCodeFeedbackOpen: true,
    });
    return true;
  },
  abortController: null,
  setAbortController: (controller) => set({ abortController: controller }),
}));

export default useSelectedStore;
